import { AdminLayout } from "./AdminLayout";
import { useState } from "react";
import { Plus, X, AlertCircle, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const Springboard = () => {
  const [step, setStep] = useState('form'); // 'form' | 'clarification' | 'success'
  
  const [formData, setFormData] = useState({
    oneLineIdea: '',
    targetUser: '',
    coreProblem: '',
    mustHaveOutcomes: ['', '', ''],
    constraints: '',
    nonGoals: ['']
  });
  
  const [clarificationData, setClarificationData] = useState({
    questions: [],
    answers: [],
    reasons: []
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.oneLineIdea.trim()) {
      newErrors.oneLineIdea = 'One-line idea is required';
    } else if (formData.oneLineIdea.length > 200) {
      newErrors.oneLineIdea = 'Must be 200 characters or less';
    }
    
    if (!formData.targetUser.trim()) {
      newErrors.targetUser = 'Target user is required';
    }
    
    if (!formData.coreProblem.trim()) {
      newErrors.coreProblem = 'Core problem is required';
    }
    
    const validOutcomes = formData.mustHaveOutcomes.filter(o => o.trim());
    if (validOutcomes.length < 3) {
      newErrors.mustHaveOutcomes = 'At least 3 must-have outcomes required';
    } else if (validOutcomes.length > 7) {
      newErrors.mustHaveOutcomes = 'Maximum 7 must-have outcomes allowed';
    }
    
    if (!formData.constraints.trim()) {
      newErrors.constraints = 'Constraints are required (tech, budget, or legal)';
    }
    
    const validNonGoals = formData.nonGoals.filter(ng => ng.trim());
    if (validNonGoals.length === 0) {
      newErrors.nonGoals = 'At least one non-goal is required. What are you NOT building?';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInitialSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API}/springboard/projects`,
        {
          one_line_idea: formData.oneLineIdea,
          target_user: formData.targetUser,
          core_problem: formData.coreProblem,
          must_have_outcomes: formData.mustHaveOutcomes.filter(o => o.trim()),
          constraints: formData.constraints,
          non_goals: formData.nonGoals.filter(ng => ng.trim())
        },
        { withCredentials: true }
      );
      
      // Check if clarification is required
      if (response.data.requires_clarification) {
        // Store clarification questions
        setClarificationData({
          questions: response.data.questions,
          answers: new Array(response.data.questions.length).fill(''),
          reasons: response.data.reasons
        });
        setStep('clarification');
        toast.info('Please provide more details to continue');
      } else {
        // Project created successfully
        toast.success('Project created successfully!');
        setStep('success');
        resetForm();
      }
      
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClarificationSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all answers are filled
    const emptyAnswers = clarificationData.answers.filter(a => !a.trim());
    if (emptyAnswers.length > 0) {
      toast.error('Please answer all clarification questions');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${API}/springboard/projects/with-clarifications`,
        {
          one_line_idea: formData.oneLineIdea,
          target_user: formData.targetUser,
          core_problem: formData.coreProblem,
          must_have_outcomes: formData.mustHaveOutcomes.filter(o => o.trim()),
          constraints: formData.constraints,
          non_goals: formData.nonGoals.filter(ng => ng.trim()),
          clarification_questions: clarificationData.questions,
          clarification_answers: clarificationData.answers
        },
        { withCredentials: true }
      );
      
      toast.success('Project created successfully with clarifications!');
      setStep('success');
      resetForm();
      
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      oneLineIdea: '',
      targetUser: '',
      coreProblem: '',
      mustHaveOutcomes: ['', '', ''],
      constraints: '',
      nonGoals: ['']
    });
    setClarificationData({
      questions: [],
      answers: [],
      reasons: []
    });
    setErrors({});
  };

  const handleBackToForm = () => {
    setStep('form');
  };

  const handleStartNew = () => {
    resetForm();
    setStep('form');
  };

  const updateClarificationAnswer = (index, value) => {
    const newAnswers = [...clarificationData.answers];
    newAnswers[index] = value;
    setClarificationData({ ...clarificationData, answers: newAnswers });
  };

  // Helper functions for form management
  const addOutcome = () => {
    if (formData.mustHaveOutcomes.length < 7) {
      setFormData({
        ...formData,
        mustHaveOutcomes: [...formData.mustHaveOutcomes, '']
      });
    }
  };

  const removeOutcome = (index) => {
    if (formData.mustHaveOutcomes.length > 3) {
      setFormData({
        ...formData,
        mustHaveOutcomes: formData.mustHaveOutcomes.filter((_, i) => i !== index)
      });
    }
  };

  const updateOutcome = (index, value) => {
    const newOutcomes = [...formData.mustHaveOutcomes];
    newOutcomes[index] = value;
    setFormData({ ...formData, mustHaveOutcomes: newOutcomes });
  };

  const addNonGoal = () => {
    setFormData({
      ...formData,
      nonGoals: [...formData.nonGoals, '']
    });
  };

  const removeNonGoal = (index) => {
    if (formData.nonGoals.length > 1) {
      setFormData({
        ...formData,
        nonGoals: formData.nonGoals.filter((_, i) => i !== index)
      });
    }
  };

  const updateNonGoal = (index, value) => {
    const newNonGoals = [...formData.nonGoals];
    newNonGoals[index] = value;
    setFormData({ ...formData, nonGoals: newNonGoals });
  };

  // Render success message
  if (step === 'success') {
    return (
      <AdminLayout title="Springboard">
        <div className="space-y-6" data-testid="springboard-success">
          <div className="card-dashboard text-center py-12">
            <div className="text-green-400 text-5xl mb-4">✓</div>
            <h3 className="text-xl font-bold mb-2">Project Created Successfully!</h3>
            <p className="text-[#8A8A8A] mb-6">Your project has been created with a draft specification.</p>
            <button
              onClick={handleStartNew}
              className="btn-primary text-sm"
            >
              Start Another Project
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Render clarification step
  if (step === 'clarification') {
    return (
      <AdminLayout title="Springboard">
        <div className="space-y-6" data-testid="springboard-clarification">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold">Clarification Needed</h2>
              <p className="text-sm text-[#8A8A8A]">Your input needs more detail. Please answer these questions.</p>
            </div>
          </div>

          {/* Reasons why clarification is needed */}
          <div className="card-dashboard border-2 border-yellow-500/20 bg-yellow-500/5">
            <h3 className="text-sm font-medium mb-2 text-yellow-400">⚠️ Why clarification is needed:</h3>
            <ul className="text-sm text-[#8A8A8A] space-y-1">
              {clarificationData.reasons.map((reason, idx) => (
                <li key={idx}>• {reason}</li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleClarificationSubmit} className="space-y-4">
            {clarificationData.questions.map((question, index) => (
              <div key={index} className="card-dashboard">
                <label className="block text-sm font-medium mb-2">
                  Question {index + 1} <span className="text-red-400">*</span>
                </label>
                <p className="text-[#8A8A8A] text-sm mb-3">{question}</p>
                <textarea
                  rows={3}
                  value={clarificationData.answers[index]}
                  onChange={(e) => updateClarificationAnswer(index, e.target.value)}
                  placeholder="Your answer..."
                  className="input-field resize-none"
                  required
                />
              </div>
            ))}

            <div className="flex justify-between gap-3">
              <button
                type="button"
                onClick={handleBackToForm}
                className="btn-secondary text-sm"
              >
                Back to Form
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary text-sm px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating Project...' : 'Submit Clarifications'}
              </button>
            </div>
          </form>
        </div>
      </AdminLayout>
    );
  }

  // Render initial form
  return (
    <AdminLayout title="Springboard">
      <div className="space-y-6" data-testid="springboard-module">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Start New Project</h2>
            <p className="text-sm text-[#8A8A8A]">Define your project scope clearly and explicitly</p>
          </div>
        </div>

        <form onSubmit={handleInitialSubmit} className="space-y-6">
          {/* One-line Idea */}
          <div className="card-dashboard">
            <label className="block text-sm font-medium mb-2">
              One-line Idea <span className="text-red-400">*</span>
              <span className="text-[#8A8A8A] font-normal ml-2">
                ({formData.oneLineIdea.length}/200 chars)
              </span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={formData.oneLineIdea}
              onChange={(e) => setFormData({...formData, oneLineIdea: e.target.value})}
              placeholder="e.g., A task management app for remote teams"
              className="input-field"
            />
            {errors.oneLineIdea && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.oneLineIdea}
              </p>
            )}
          </div>

          {/* Target User */}
          <div className="card-dashboard">
            <label className="block text-sm font-medium mb-2">
              Target User <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.targetUser}
              onChange={(e) => setFormData({...formData, targetUser: e.target.value})}
              placeholder="e.g., Remote engineering teams of 5-50 people"
              className="input-field"
            />
            {errors.targetUser && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.targetUser}
              </p>
            )}
          </div>

          {/* Core Problem */}
          <div className="card-dashboard">
            <label className="block text-sm font-medium mb-2">
              Core Problem <span className="text-red-400">*</span>
            </label>
            <textarea
              rows={3}
              value={formData.coreProblem}
              onChange={(e) => setFormData({...formData, coreProblem: e.target.value})}
              placeholder="What specific problem does this solve? Be concrete."
              className="input-field resize-none"
            />
            {errors.coreProblem && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.coreProblem}
              </p>
            )}
          </div>

          {/* Must-have Outcomes */}
          <div className="card-dashboard">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">
                Must-Have Outcomes <span className="text-red-400">*</span>
                <span className="text-[#8A8A8A] font-normal ml-2">(3-7 required)</span>
              </label>
              {formData.mustHaveOutcomes.length < 7 && (
                <button
                  type="button"
                  onClick={addOutcome}
                  className="text-sm text-[#5F8FB8] hover:text-[#4A7A9F] flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              )}
            </div>
            <div className="space-y-2">
              {formData.mustHaveOutcomes.map((outcome, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={outcome}
                    onChange={(e) => updateOutcome(index, e.target.value)}
                    placeholder={`Outcome ${index + 1}`}
                    className="input-field flex-1"
                  />
                  {formData.mustHaveOutcomes.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removeOutcome(index)}
                      className="p-2 hover:bg-[#2E2E2E] rounded"
                    >
                      <X className="w-4 h-4 text-[#8A8A8A]" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.mustHaveOutcomes && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.mustHaveOutcomes}
              </p>
            )}
          </div>

          {/* Constraints */}
          <div className="card-dashboard">
            <label className="block text-sm font-medium mb-2">
              Constraints <span className="text-red-400">*</span>
              <span className="text-[#8A8A8A] font-normal ml-2">(tech, budget, legal)</span>
            </label>
            <textarea
              rows={3}
              value={formData.constraints}
              onChange={(e) => setFormData({...formData, constraints: e.target.value})}
              placeholder="e.g., Must work on mobile, $5k budget, GDPR compliant"
              className="input-field resize-none"
            />
            {errors.constraints && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.constraints}
              </p>
            )}
          </div>

          {/* Non-Goals (REQUIRED) */}
          <div className="card-dashboard border-2 border-yellow-500/20 bg-yellow-500/5">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">
                Explicit Non-Goals <span className="text-red-400">*</span>
                <span className="text-yellow-400 font-normal ml-2">(Required - What are you NOT building?)</span>
              </label>
              <button
                type="button"
                onClick={addNonGoal}
                className="text-sm text-[#5F8FB8] hover:text-[#4A7A9F] flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {formData.nonGoals.map((nonGoal, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={nonGoal}
                    onChange={(e) => updateNonGoal(index, e.target.value)}
                    placeholder="e.g., Mobile native apps, Real-time collaboration"
                    className="input-field flex-1"
                  />
                  {formData.nonGoals.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeNonGoal(index)}
                      className="p-2 hover:bg-[#2E2E2E] rounded"
                    >
                      <X className="w-4 h-4 text-[#8A8A8A]" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {errors.nonGoals && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {errors.nonGoals}
              </p>
            )}
            <p className="text-xs text-yellow-400 mt-3">
              ⚠️ Non-goals prevent scope drift. Explicitly state what you're NOT building.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary text-sm px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? 'Validating...' : (
                <>
                  Continue <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default Springboard;
